<template>
    <div class="prize-page">
        <div v-if="selectedFaceCard" class="facecard-container">
            <ContainerObject :containerObject="selectedFaceCard" @event="customEvent($event)" />
        </div>
        <div v-else-if="!loading" class="no-facecard">
            <p>No facecard available for this prize</p>
        </div>
        <LoadingComponent v-if="loading" />
    </div>
</template>

<script lang="ts">
import { defineComponent } from "vue"
import { ActionType, Container } from "@/types/customPage"
import { VItem, ItemStatus, PrizeStatus } from "@/types/vItem"
import { Prize, RedemptionType } from "@/types/prize"
import { emptyContainerWithMessage } from "@/utils/container"
import { vItemStore } from "@/store/pinia_modules/vItemStore"
import { authStore } from "@/store/pinia_modules/authStore"
import { prizeStore } from "@/store/pinia_modules/prizeStore"
import { dropPoolStore } from "@/store/pinia_modules/dropPoolStore"
import { categoryStore } from "@/store/pinia_modules/categoryStore"
import { campaignStore } from "@/store/pinia_modules/campaignStore"
import { navigationStore } from "@/store/pinia_modules/navigationStore"

export default defineComponent({
    data() {
        return {
            selectedFaceCard: null as Container | null,
            selectedItem: null as VItem | null,
            loading: true,
            initializingPrize: false,
        }
    },
    async mounted() {
        await this.initializePrize()
    },

  watch: {
    "$route.params.vitemID"(newVitemID, oldVitemID) {
      // Prize route hydration is intentionally route-driven: the AR/proxy component is shared
      // across multiple views, so this page must react to prize route changes without relying
      // on selectedItem mutations that can be triggered by store refreshes or cache revalidation.
      if (newVitemID && newVitemID !== oldVitemID) {
        void this.initializePrize();
      }
    },
  },

    methods: {
        async initializePrize() {
            if (this.initializingPrize) return;
            this.initializingPrize = true;
            this.loading = true
            try {

            // Validate campaign ID first
            const campaignId = this.$route.params.campaign as string
            if (!campaignId || campaignId === 'error') {
                console.error("Invalid campaign ID:", campaignId)
                return
            }

            if (campaignStore().campaign?.id !== campaignId) {
                let campaignIdentifier: {
                    subdomain: string | null;
                    campaignId: string | null;
                } = { subdomain: null, campaignId: null };

                if (campaignId) {
                    campaignIdentifier.campaignId = campaignId;
                }
                console.log("PrizePage: campaignIdentifier", campaignIdentifier)
                const campaignData = await campaignStore().getInitialCampaignData(campaignIdentifier)
                campaignStore().setCampaign(campaignData.campaign)
            }

            // Get vitem ID from route params
            const vitemID = this.$route.params.vitemID as string
            if (!vitemID) {
                console.error("No vitem ID provided")
                return
            }

            // Find the vitem
            await vItemStore().getItems()
            const item = vItemStore().items.find(item => item.id === vitemID)
            if (!item) {
                console.error("VItem not found:", vitemID)
                return
            }

            this.selectedItem = item
            vItemStore().selectItem(item.id!)

            await prizeStore().getPrizes()
            // Get the prize and determine which facecard to show
            const prize = this.getPrizeByID(item.prizeId!)
            if (!prize) {
                console.error("Prize not found:", item.prizeId)
                return
            }

            // Determine by vitem status
            switch (item.itemStatus) {
                case ItemStatus.CLAIMED:
                    if (this.hasAllocatedReward(item)) {
                        await this.setupRedemptionFacecard(prize)
                    } else {
                        await this.setupGameplayFacecard(prize)
                    }
                    break
                case ItemStatus.ALLOCATED:
                    await this.setupRedemptionFacecard(prize)
                    break
                case ItemStatus.NOPRIZE:
                    let popupname = "already-redeemed"
                    switch (prize.redemptionType) {
                        case RedemptionType.VOUCHER:
                            popupname = "already-redeemed-voucher"
                            break
                        case RedemptionType.TAP_REDEEM:
                            popupname = "already-redeemed-tap-to-redeem"
                            break
                        case RedemptionType.SCAN:
                            popupname = "already-redeemed-qr"
                            break
                        case RedemptionType.LINK_VOUCHER:
                            popupname = "already-redeemed-url-voucher"
                            break
                        case RedemptionType.YOYO_COUPON:
                            popupname = "already-redeemed-yoyo-coupon"
                            break
                        case RedemptionType.MOMINT_NFT:
                            popupname = "already-redeemed-momint-nft"
                            break
                    }
                    this.$router.push(`/${authStore().getCampaignID!}/vault?popup=${popupname}`)
                    break
                case ItemStatus.REDEEMED:
                    await this.setupRedemptionFacecard(prize)
                    break
                default:
                    console.error("Unknown vitem status:", item.itemStatus)
                    break
            }
            } catch (error) {
                console.error("Failed to initialize prize page", error)
            } finally {
                this.initializingPrize = false
                this.loading = false
                // Client-side navigation into the prize route can leave the global app loader active.
                // Hard refresh clears it through App.init(), but this route must also clear it after hydration.
                navigationStore().setloading(false)
            }
        },

        hasAllocatedReward(item: VItem): boolean {
            // Proxy/gameplay claim flows can return a claimed vitem that already has the
            // reward allocated. In that case the prize route must render the redemption
            // facecard directly instead of replaying the gameplay facecard.
            return Boolean(
                item.voucherCode ||
                item.voucherCodeId ||
                item.prizeStatus === PrizeStatus.ALLOCATED
            )
        },

        async setupGameplayFacecard(prize: Prize) {
            // Set up click to play state
            if (prize.clickToPlay) {
                prizeStore().saveClickToPlay(prize.clickToPlay)
            } else {
                prizeStore().saveClickToPlay(false)
            }

            // The claimed vitem is the durable outcome authority. Pool strategy
            // and catalogue metadata describe configuration, not this claim.
            if (this.selectedItem?.winning === false) {
                prizeStore().saveIsWinning(false)
                this.selectedFaceCard = prize.losingGameplayFaceCard || emptyContainerWithMessage("No Facecard", "Losing facecard not configured")
            } else {
                prizeStore().saveIsWinning(true)
                this.selectedFaceCard = prize.gameplayFaceCard || emptyContainerWithMessage("No Facecard", "Gameplay facecard not configured")
            }
        },

        async setupRedemptionFacecard(prize: Prize) {
            if (this.selectedItem?.winning === false) {
                await this.setupGameplayFacecard(prize)
                return
            }
            this.selectedFaceCard = prize.redemptionFaceCard as Container
            this.ensureRedemptionPrimaryAction(this.selectedFaceCard)
            this.populateVoucherCode()
        },

        ensureRedemptionPrimaryAction(container: Container | null) {
            if (!container) return

            const isConfiguredAction = (action: { type?: ActionType | null } | null | undefined) =>
                Boolean(action?.type && action.type !== ActionType.NONE)

            const visit = (node: Container) => {
                const configuredAction =
                    isConfiguredAction(node.action) ||
                    Boolean(node.actions?.some((action) => isConfiguredAction(action)))

                if (node.attributes?.id === "prize-primary-button" && !configuredAction) {
                    const action = { type: ActionType.REDEEM_ITEM, value: null }
                    node.action = action
                    node.actions = [action]
                }

                node.elements?.forEach(visit)
                node.containers?.forEach(visit)
            }

            visit(container)
        },

        async customEvent($event: any) {
            console.log("Prize.vue customEvent received:", $event, "Item status:", this.selectedItem?.itemStatus)

            if ($event == "redeem" && this.selectedItem) {
                // Only allow redemption if item is in CLAIMED status (not already ALLOCATED/REDEEMED)
                if (this.selectedItem.itemStatus === ItemStatus.ALLOCATED) {
                    console.log("Processing redeem action for item:", this.selectedItem.id)
                    this.setupRedemptionFacecard(this.getPrizeByID(this.selectedItem.prizeId)!);
                } else {
                    console.warn("Cannot redeem item - item status is:", this.selectedItem.itemStatus)
                }
            } else if ($event == "losingPrize" && this.selectedItem) {
                await vItemStore().getItems()
                const campaignId = authStore().getCampaignID
                this.$router.push(`/${campaignId}/vault`)
            } else if ($event == "close" || $event == "back") {
                // Navigate back to vault
                const campaignId = authStore().getCampaignID
                this.$router.push(`/${campaignId}/vault`)
            }
        },

        populateVoucherCode() {
            setTimeout(() => {
                const voucherCodes = document.querySelectorAll(
                    ".voucher-code"
                ) as any
                const voucherCode = document.querySelector(
                    "#voucher-code"
                ) as HTMLElement
                if (voucherCode) {
                    voucherCode.innerText = this.selectedItem!.voucherCode!
                }
                if (voucherCodes) {
                    for (let i = 0; i < voucherCodes.length; i++) {
                        voucherCodes[i].innerText =
                            this.selectedItem!.voucherCode!
                    }
                }
            }, 100)
        },


        getPrizeByID(id: string): Prize | undefined {
            return prizeStore().getPrizeByID(id)
        },

        getPoolByID(id: string) {
            return dropPoolStore().getPoolByID(id)
        },

        getCategoryByID(id: string) {
            return categoryStore().getItemByID(id)
        },
    },
})
</script>

<style scoped lang="scss">
.prize-page {
    position: fixed;
    display: flex;
    width: 100%;
    height: 100%;
    z-index: 100;
    top: 0;
    left: 0;
    background-color: #fff;
    justify-content: center;
    align-items: center;
}

.facecard-container {
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
}

.no-facecard {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;

    p {
        font-size: 18px;
        color: #666;
        text-align: center;
        margin: 20px;
    }
}
</style>
